import { PencilLine } from 'lucide-react';
import { Button, Card, EmptyState } from '@/shared/components';
import { useStartDraftAction } from '../hooks/useStartDraftAction';
import { useTemplatePreview } from '../hooks/useTemplatePreview';
import type { DocumentTemplateDetail } from '../types/documentTemplates.types';
import { DraftEditor } from './DraftEditor/DraftEditor';
import { TemplatePreviewModal } from './TemplatePreviewModal';

interface Props {
  template: DocumentTemplateDetail;
  canEdit: boolean;
}

function NoDraft({ template, canEdit }: Props) {
  const { start, isStarting } = useStartDraftAction(template.id);
  const preview = useTemplatePreview(template.id);
  const { published } = template;

  return (
    <Card>
      <EmptyState
        icon={PencilLine}
        title="No draft in progress"
        description={
          <>
            {published ? `Version ${published.versionNumber} is live. ` : 'Nothing has been published yet. '}
            {canEdit
              ? 'Start a draft to make changes. Nothing changes for customers until the draft is published.'
              : 'There are no unpublished changes to review.'}
          </>
        }
        actions={
          (published || canEdit) && (
            <>
              {published && (
                <Button type="button" variant="neutral" appearance="outline" size="sm" onClick={() => preview.open({ versionId: published.id })}>
                  Preview live version
                </Button>
              )}
              {canEdit && (
                <Button type="button" size="sm" onClick={() => start()} disabled={isStarting}>
                  {isStarting ? 'Starting...' : 'Start draft'}
                </Button>
              )}
            </>
          )
        }
      />
      <TemplatePreviewModal
        isOpen={preview.isOpen}
        onClose={preview.close}
        isLoading={preview.isLoading}
        error={preview.error}
        preview={preview.preview}
      />
    </Card>
  );
}

export function DraftTab({ template, canEdit }: Props) {
  if (template.draft) {
    return (
      <DraftEditor
        key={template.draft.id}
        templateId={template.id}
        draft={template.draft}
        canEdit={canEdit}
        supersedesVersionNumber={template.published?.versionNumber ?? null}
      />
    );
  }

  return <NoDraft template={template} canEdit={canEdit} />;
}
